import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { ArrowUpFromLine, Pencil } from 'lucide-react';
import { api, apiError } from '@/lib/api';
import { haptic, showConfirm } from '@/lib/telegram';
import { money, parseAmountInput } from '@/lib/format';
import { useI18n } from '@/i18n';
import { useAuth } from '@/context/AuthContext';
import { AmountField, InfoRow, MethodIcon, Notice, PageLoading, PageTitle, Spinner } from '@/components/ui';
import { PixProfileForm } from '@/components/PixProfileForm';
import { NeedAccount } from './NeedAccount';
import type { Method, PixProfile, Tx } from '@/types/api';

function BalanceLine() {
    const { t, lang } = useI18n();
    const q = useQuery({ queryKey: ['balance'], queryFn: async () => (await api.get<{ balance: number; currency: string }>('/balance')).data, staleTime: 20_000 });
    return (
        <div className="card-lime py-3 flex items-center justify-between">
            <span className="text-xs text-ink-muted">{t('withdraw.available')}</span>
            <span className="text-lg font-black font-display text-lime">{q.data ? money(q.data.balance, q.data.currency, lang) : '…'}</span>
        </div>
    );
}

/**
 * The common part of every withdrawal: amount, fee / net / balance debit preview,
 * Telegram confirmation, then `send(amount)`.
 */
function WithdrawForm({ method, before, after, ready = true, send }: {
    method: Method; before?: React.ReactNode; after?: React.ReactNode; ready?: boolean; send: (amount: number) => Promise<Tx>;
}) {
    const { t, te, lang } = useI18n();
    const { me } = useAuth();
    const navigate = useNavigate();
    const qc = useQueryClient();
    const [text, setText] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const amount = parseAmountInput(text);
    const board = me?.currency || 'BRL';
    const rangeError = text && (!Number.isFinite(amount) ? t('errors.amount_invalid')
        : amount < method.min || (method.max > 0 && amount > method.max) ? t('errors.amount_range', { min: money(method.min, method.currency, lang), max: money(method.max, method.currency, lang) }) : null);
    const ok = Number.isFinite(amount) && amount > 0 && !rangeError;
    const net = ok ? Math.round((amount - amount * method.feePercent / 100) * 100) / 100 : 0;
    const debit = ok ? Math.round(amount * method.rate * 100) / 100 : 0;

    const submit = async () => {
        if (!ok || !ready) { haptic('error'); return; }
        if (!(await showConfirm(t('withdraw.confirm', { amount: money(amount, method.currency, lang), net: money(net, method.currency, lang), method: method.name })))) return;
        setBusy(true); setError(null);
        try {
            const tx = await send(amount);
            haptic('success');
            void qc.invalidateQueries({ queryKey: ['transactions'] });
            void qc.invalidateQueries({ queryKey: ['balance'] });
            navigate(`/tx/${tx.operationId}`, { replace: true });
        } catch (e) {
            const err = apiError(e);
            setError(te(err, {
                currency: method.currency,
                balance: typeof err.balance === 'number' ? money(err.balance, board, lang) : '',
                required: typeof err.required === 'number' ? money(err.required, board, lang) : '',
                limit: typeof err.limit === 'number' ? money(err.limit, board, lang) : '',
                remaining: typeof err.remaining === 'number' ? money(err.remaining, board, lang) : '',
                hours: typeof err.windowHours === 'number' ? err.windowHours : (typeof err.hours === 'number' ? err.hours : '')
            }));
            haptic('error');
        } finally { setBusy(false); }
    };

    return (
        <div className="space-y-3">
            {before}
            <AmountField value={text} onChange={v => { setText(v); setError(null); }} currency={method.currency} min={method.min} max={method.max} error={rangeError || null} />
            {after}
            <div className="card py-3">
                <InfoRow label={t('withdraw.limits')} value={`${money(method.min, method.currency, lang)} – ${money(method.max, method.currency, lang)}`} />
                {method.feePercent > 0 && <InfoRow label={t('withdraw.fee')} value={`${method.feePercent}%`} />}
                {ok && <InfoRow label={t('withdraw.youReceive')} value={money(net, method.currency, lang)} strong />}
                {ok && method.currency !== board && <InfoRow label={t('withdraw.debit')} value={money(debit, board, lang)} />}
            </div>
            {method.instructions && <Notice>{method.instructions}</Notice>}
            <Notice>{t('withdraw.approvalNote')}</Notice>
            {error && <Notice tone="warn">{error}</Notice>}
            <button className="btn-primary w-full" disabled={busy || !ok || !ready} onClick={submit}>
                {busy ? <Spinner className="w-4 h-4" /> : <ArrowUpFromLine className="w-4 h-4" />} {t('withdraw.cta')}
            </button>
        </div>
    );
}

function PixWithdraw({ method }: { method: Method }) {
    const { t, te } = useI18n();
    const [editing, setEditing] = useState(false);
    const q = useQuery({ queryKey: ['pix', 'profile'], queryFn: async () => (await api.get<PixProfile>('/pix/profile')).data });
    if (q.isLoading) return <PageLoading />;
    if (q.isError || !q.data) return <Notice tone="warn">{te(apiError(q.error))}</Notice>;
    const p = q.data;
    if (p.needed.withdraw.length || editing) {
        return <PixProfileForm profile={p} fields={editing ? ['name', 'cpf'] : p.needed.withdraw} intro={t('pix.withdrawIntro')} onSaved={() => setEditing(false)} />;
    }
    const payee = (
        <div className="card py-3 flex items-center gap-3">
            <div className="flex-1 min-w-0">
                <div className="text-[11px] text-ink-muted">{t('pix.payee')}</div>
                <div className="text-sm font-semibold truncate">{p.profile.name}</div>
                <div className="text-[11px] text-ink-muted font-mono">{t('pix.pixKey')}: CPF {p.profile.cpf}</div>
            </div>
            <button className="btn-secondary px-3 py-2 text-xs" onClick={() => setEditing(true)}><Pencil className="w-3.5 h-3.5" /> {t('common.edit')}</button>
        </div>
    );
    return <WithdrawForm method={method} before={payee} after={<Notice tone="warn">{t('pix.cpfKeyHint')}</Notice>}
                         send={async amount => (await api.post<Tx>('/withdraw/pix', { amount })).data} />;
}

function CryptoWithdraw({ method }: { method: Method }) {
    const { t } = useI18n();
    const coins = method.coins || [];
    const [coin, setCoin] = useState(coins[0]?.code || '');
    const [address, setAddress] = useState('');
    const c = coins.find(x => x.code === coin);
    const before = (
        <div>
            <label className="label">{t('withdraw.crypto.coin')}</label>
            <div className="grid grid-cols-2 gap-2">
                {coins.map(x => (
                    <button key={x.code} onClick={() => { haptic('selection'); setCoin(x.code); }}
                        className={clsx('rounded-xl border px-3 py-2.5 text-left transition active:scale-[.98]', coin === x.code ? 'border-brand-400/60 bg-brand-400/10' : 'border-white/10 bg-white/[.03]')}>
                        <div className="text-sm font-bold">{x.label}</div>
                        <div className="text-[11px] text-ink-muted">{x.network}</div>
                    </button>
                ))}
            </div>
        </div>
    );
    const after = (
        <div>
            <label className="label">{t('withdraw.crypto.address', { coin: c?.label || '', network: c?.network || '' })}</label>
            <input className="input font-mono text-sm" autoCapitalize="none" autoCorrect="off" spellCheck={false} value={address} onChange={e => setAddress(e.target.value.trim())} />
            <p className="text-[11px] text-amber-200/90 mt-1.5 px-0.5">{t('withdraw.crypto.warning', { network: c?.network || '' })}</p>
        </div>
    );
    return <WithdrawForm method={method} before={before} after={after} ready={!!coin && address.length >= 10}
                         send={async amount => (await api.post<Tx>('/withdraw/crypto', { coin, amount, address })).data} />;
}

function ManualWithdraw({ method }: { method: Method }) {
    const fields = method.fields || [];
    const [values, setValues] = useState<Record<string, string>>({});
    const ready = fields.every(f => f.optional || String(values[f.key] || '').trim());
    const after = (
        <div className="space-y-3">
            {fields.map(f => (
                <div key={f.key}>
                    <label className="label">{f.label}{f.optional ? '' : ' *'}</label>
                    <input className={clsx('input', f.key === 'bep20Wallet' && 'font-mono text-sm')} autoCapitalize="none" autoCorrect="off" spellCheck={false}
                           value={values[f.key] || ''} onChange={e => setValues(s => ({ ...s, [f.key]: e.target.value }))} />
                    <p className="text-[11px] text-ink-muted mt-1 px-0.5">{f.prompt}</p>
                </div>
            ))}
        </div>
    );
    return <WithdrawForm method={method} after={after} ready={ready}
                         send={async amount => (await api.post<Tx>(`/withdraw/manual/${encodeURIComponent(method.key)}`, { amount, fields: values })).data} />;
}

export function WithdrawMethodPage() {
    const { key } = useParams();
    const { t, te, lang } = useI18n();
    const { me } = useAuth();
    const q = useQuery({
        queryKey: ['methods', 'withdraw', lang],
        queryFn: async () => (await api.get<{ methods: Method[] }>('/withdraw/methods')).data.methods,
        enabled: !!me?.registered, staleTime: 30_000
    });
    const method = useMemo(() => q.data?.find(m => m.key === key), [q.data, key]);
    if (!me?.registered) return <NeedAccount />;
    if (q.isLoading) return <PageLoading />;
    if (q.isError) return <Notice tone="warn">{te(apiError(q.error))}</Notice>;
    if (!method) return <Notice tone="warn">{t('errors.method_unavailable')}</Notice>;
    return (
        <div className="space-y-3">
            <div className="flex items-center gap-3">
                <MethodIcon kind={method.kind} size="lg" />
                <PageTitle title={method.name} subtitle={t(`kinds.${method.kind}`)} />
            </div>
            <BalanceLine />
            {method.kind === 'pix' && <PixWithdraw method={method} />}
            {method.kind === 'crypto' && <CryptoWithdraw method={method} />}
            {(method.kind === 'manual' || method.kind === 'usdt') && <ManualWithdraw method={method} />}
        </div>
    );
}
